/**
 * Recurring Invoice schedules + Bulk-run + Branding Studio
 * (Phase AC.2 admin UI). Sits beside `InvoicesSection`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

const FREQ_OPTIONS = [
  ['weekly',        'Weekly'],
  ['fortnightly',   'Fortnightly'],
  ['every_4_weeks', 'Every 4 weeks'],
  ['monthly',       'Monthly'],
  ['day_of_month',  'Specific day of month'],
];

const fmtMoney = (v) => (v == null ? '—' : '£' + Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtDT = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const isoTodayStart = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); };
const isoTodayEnd = () => { const d = new Date(); d.setHours(23, 59, 59, 0); return d.toISOString(); };

// =========================================================================
// Recurring schedules
// =========================================================================
export function RecurringSchedulesSection({ parents = [] }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listInvoiceSchedules();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) { toast.error(err?.message || 'Failed to load schedules'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (row) => {
    try {
      await api.updateInvoiceSchedule(row._id, { is_active: !row.is_active });
      toast.success(row.is_active ? 'Paused' : 'Reactivated');
      load();
    } catch (err) { toast.error(err?.message || 'Failed'); }
  };
  const delSchedule = async (row) => {
    if (!window.confirm(`Delete this schedule? Existing invoices are kept.`)) return;
    try { await api.deleteInvoiceSchedule(row._id); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err?.message || 'Failed'); }
  };
  const runNow = async () => {
    try { const r = await api.tickInvoiceSchedules(); toast.success(`Ran ${r.stats?.run || 0}, skipped ${r.stats?.skipped || 0}`); load(); }
    catch (err) { toast.error(err?.message || 'Failed'); }
  };

  return (
    <div className="card" data-testid="recurring-schedules-card" style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0 }}>🔁 Recurring invoices</h3>
          <p style={{ margin: '0.15rem 0 0', color: 'hsl(var(--muted-foreground))', fontSize: '0.82rem' }}>
            Auto-generate invoices on a schedule. The system wakes every 15 minutes and materialises anything due.
          </p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={runNow} data-testid="recurring-run-now" style={{ fontSize: '0.82rem' }}>▶ Run due now</button>
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)} data-testid="recurring-new-btn">➕ New schedule</button>
      </div>

      {loading ? (
        <div className="loading" data-testid="recurring-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state" data-testid="recurring-empty">
          No recurring schedules yet. Create one to bill a parent automatically each week / month.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }} data-testid="recurring-table">
            <thead>
              <tr style={{ borderBottom: '2px solid hsl(var(--border))', textAlign: 'left' }}>
                <th style={{ padding: '0.4rem 0.5rem' }}>Parent</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Frequency</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Next run</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Last run</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Runs</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Status</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id} data-testid={`recurring-row-${r._id}`} style={{ borderTop: '1px solid hsl(var(--border))' }}>
                  <td style={{ padding: '0.5rem' }}>
                    {r.parent_name || r.parent_user_id?.slice(0, 8)}
                    {r.parent_email && <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>{r.parent_email}</div>}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    {(FREQ_OPTIONS.find(([k]) => k === r.frequency) || [])[1] || r.frequency}
                    {r.frequency === 'day_of_month' && r.anchor_day_of_month ? ` · day ${r.anchor_day_of_month}` : ''}
                  </td>
                  <td style={{ padding: '0.5rem' }}>{fmtDT(r.next_run_at)}</td>
                  <td style={{ padding: '0.5rem' }}>{fmtDT(r.last_run_at)}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{r.run_count || 0}</td>
                  <td style={{ padding: '0.5rem' }}>
                    <span style={{ display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700,
                      background: r.is_active ? 'hsl(140 60% 92%)' : 'hsl(0 0% 92%)',
                      color: r.is_active ? 'hsl(140 45% 26%)' : 'hsl(0 0% 40%)' }}>
                      {r.is_active ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button type="button" onClick={() => toggle(r)} className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }} data-testid={`recurring-toggle-${r._id}`}>
                      {r.is_active ? '⏸ Pause' : '▶ Resume'}
                    </button>
                    <button type="button" onClick={() => delSchedule(r)} className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', color: 'hsl(0 60% 40%)' }} data-testid={`recurring-delete-${r._id}`}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <ScheduleEditor parents={parents} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function ScheduleEditor({ parents, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    parent_user_id: '',
    frequency: 'monthly',
    anchor_day_of_month: 1,
    start_date: new Date().toISOString().slice(0, 10),
    issue_immediately: true,
    include_bookings_in_period: true,
    terms_label: 'Monthly',
    description: '',
    rate: 0,
    notes: '',
  });

  const save = async () => {
    if (!form.parent_user_id) { toast.error('Choose a parent'); return; }
    if (!form.include_bookings_in_period && (!form.description || Number(form.rate) <= 0)) {
      toast.error('Add a fixed line item OR enable "Include bookings"');
      return;
    }
    setSaving(true);
    try {
      const template = {
        line_items: (form.description && Number(form.rate) > 0)
          ? [{ description: form.description, qty: 1, rate: Number(form.rate), amount: Number(form.rate) }]
          : [],
        notes: form.notes,
        terms_label: form.terms_label,
        include_bookings_in_period: form.include_bookings_in_period,
      };
      await api.createInvoiceSchedule({
        parent_user_id: form.parent_user_id,
        frequency: form.frequency,
        anchor_day_of_month: form.frequency === 'day_of_month' || form.frequency === 'monthly' ? Number(form.anchor_day_of_month) : undefined,
        start_date: `${form.start_date}T00:00:00`,
        issue_immediately: form.issue_immediately,
        template,
      });
      toast.success('Schedule created — next run scheduled.');
      onSaved && onSaved();
    } catch (err) { toast.error(err?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => (saving ? null : onClose())} data-testid="schedule-editor-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>➕ New recurring invoice</h2>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving} data-testid="schedule-close">✕</button>
        </div>

        <div className="form-group"><label>Parent *</label>
          <select value={form.parent_user_id} onChange={(e) => setForm({ ...form, parent_user_id: e.target.value })} data-testid="schedule-parent">
            <option value="">— select parent —</option>
            {(parents || []).map((p) => <option key={p._id} value={p._id}>{p.full_name} · {p.email}</option>)}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div className="form-group"><label>Frequency</label>
            <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="schedule-frequency">
              {FREQ_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Start date *</label>
            <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="schedule-start" />
          </div>
        </div>

        {(form.frequency === 'day_of_month' || form.frequency === 'monthly') && (
          <div className="form-group"><label>Anchor day of month (1–28 for safety)</label>
            <input type="number" min="1" max="28" value={form.anchor_day_of_month} onChange={(e) => setForm({ ...form, anchor_day_of_month: e.target.value })} data-testid="schedule-anchor-day" />
          </div>
        )}

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.include_bookings_in_period} onChange={(e) => setForm({ ...form, include_bookings_in_period: e.target.checked })} data-testid="schedule-import-bookings" />
            📥 Include this parent's confirmed bookings in each period as line items
          </label>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.issue_immediately} onChange={(e) => setForm({ ...form, issue_immediately: e.target.checked })} data-testid="schedule-auto-issue" />
            🚀 Auto-issue each invoice (assigns a number instantly). Uncheck to create drafts.
          </label>
        </div>

        <hr style={{ margin: '0.75rem 0', border: 0, borderTop: '1px solid hsl(var(--border))' }} />
        <div style={{ fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>Optional fixed line item added to every invoice (leave blank to only use bookings):</div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
          <div className="form-group"><label>Description</label>
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Monthly Childcare Retainer" data-testid="schedule-desc" />
          </div>
          <div className="form-group"><label>Rate (£)</label>
            <input type="number" step="0.01" min="0" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="schedule-rate" />
          </div>
        </div>

        <div className="form-group"><label>Notes (shown on PDF)</label>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="schedule-notes" style={{ width: '100%', padding: '0.4rem 0.5rem', fontFamily: 'inherit' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving} data-testid="schedule-cancel">Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving} data-testid="schedule-save">{saving ? 'Saving…' : '💾 Create schedule'}</button>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// Bulk Invoice Run
// =========================================================================
export function BulkInvoiceRunButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={() => setOpen(true)} data-testid="bulk-run-open">📤 Bulk invoice run</button>
      {open && <BulkRunModal onClose={() => setOpen(false)} />}
    </>
  );
}

function BulkRunModal({ onClose }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const [form, setForm] = useState({
    period_start: firstOfMonth.toISOString().slice(0, 10),
    period_end: lastOfMonth.toISOString().slice(0, 10),
    terms_label: 'Monthly',
    notes: '',
    issue_immediately: true,
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const runPreview = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.bulkRunInvoices({
        period_start: `${form.period_start}T00:00:00`,
        period_end: `${form.period_end}T23:59:59`,
        dry_run: true,
      });
      setPreview(r);
    } catch (err) { toast.error(err?.message || 'Preview failed'); }
    finally { setLoading(false); }
  };
  const execute = async () => {
    if (!preview || preview.parent_count === 0) { toast.error('Nothing to invoice — preview first'); return; }
    if (!window.confirm(`Create ${preview.parent_count} invoices totalling ${fmtMoney(preview.grand_total)}?\n\nThis cannot be undone (individual invoices can be voided).`)) return;
    setExecuting(true);
    try {
      const r = await api.bulkRunInvoices({
        period_start: `${form.period_start}T00:00:00`,
        period_end: `${form.period_end}T23:59:59`,
        dry_run: false,
        issue_immediately: form.issue_immediately,
        terms_label: form.terms_label,
        notes: form.notes,
      });
      setResult(r);
      toast.success(`Created ${r.parent_count} invoices (${fmtMoney(r.grand_total)})`);
    } catch (err) { toast.error(err?.message || 'Bulk run failed'); }
    finally { setExecuting(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => (loading || executing ? null : onClose())} data-testid="bulk-run-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>📤 Bulk invoice run</h2>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading || executing} data-testid="bulk-run-close">✕</button>
        </div>

        <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem', marginTop: '0.3rem' }}>
          Generates one invoice per parent for confirmed bookings in the chosen period. Parents with no bookings are skipped.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div className="form-group"><label>Period start</label>
            <input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="bulk-period-start" />
          </div>
          <div className="form-group"><label>Period end</label>
            <input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="bulk-period-end" />
          </div>
          <div className="form-group"><label>Terms label</label>
            <input type="text" value={form.terms_label} onChange={(e) => setForm({ ...form, terms_label: e.target.value })} data-testid="bulk-terms" />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.issue_immediately} onChange={(e) => setForm({ ...form, issue_immediately: e.target.checked })} data-testid="bulk-issue" />
              Auto-issue (assign numbers now)
            </label>
          </div>
        </div>
        <div className="form-group"><label>Notes (added to every invoice)</label>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="bulk-notes" style={{ width: '100%', padding: '0.4rem 0.5rem', fontFamily: 'inherit' }} />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" className="btn btn-secondary" onClick={runPreview} disabled={loading || executing} data-testid="bulk-preview">{loading ? 'Loading…' : '🔍 Preview'}</button>
          {preview && preview.parent_count > 0 && !result && (
            <button type="button" className="btn btn-primary" onClick={execute} disabled={executing} data-testid="bulk-execute">
              {executing ? 'Running…' : `✅ Create ${preview.parent_count} invoices · ${fmtMoney(preview.grand_total)}`}
            </button>
          )}
        </div>

        {preview && (
          <div style={{ marginTop: '0.8rem', border: '1px solid hsl(var(--border))', borderRadius: 8, overflow: 'hidden' }} data-testid="bulk-preview-table">
            <div style={{ padding: '0.5rem 0.75rem', background: 'hsl(var(--muted) / 0.4)', fontSize: '0.85rem' }}>
              <strong>{preview.parent_count}</strong> parent{preview.parent_count === 1 ? '' : 's'} · Grand total <strong>{fmtMoney(preview.grand_total)}</strong>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead><tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Parent</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Lines</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Result</th>
              </tr></thead>
              <tbody>
                {preview.per_parent.map((p) => {
                  const done = (result?.per_parent || []).find((x) => x.parent_user_id === p.parent_user_id);
                  return (
                    <tr key={p.parent_user_id} style={{ borderTop: '1px solid hsl(var(--border))' }}>
                      <td style={{ padding: '0.35rem 0.5rem' }}>{p.parent_name || p.parent_user_id.slice(0, 8)}</td>
                      <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>{p.line_count}</td>
                      <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(p.total)}</td>
                      <td style={{ padding: '0.35rem 0.5rem' }}>{done?.invoice_number ? <span style={{ color: 'hsl(140 45% 30%)' }}>✅ {done.invoice_number}</span> : done?.invoice_id ? '✅ Draft' : '—'}</td>
                    </tr>
                  );
                })}
                {preview.per_parent.length === 0 && (
                  <tr><td colSpan="4" style={{ padding: '0.75rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>No parents with bookings in this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {result && (
          <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.85rem', background: 'hsl(140 60% 96%)', border: '1px solid hsl(140 55% 82%)', borderRadius: 8, color: 'hsl(140 45% 22%)', fontSize: '0.85rem' }}>
            ✅ Created <strong>{result.parent_count}</strong> invoices · Grand total <strong>{fmtMoney(result.grand_total)}</strong>. Close this dialog and refresh the invoices list.
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Branding Studio (with live PDF preview)
// =========================================================================
export function InvoiceBrandingStudio({ organization, onSaved }) {
  const s = (organization?.settings || {});
  const [form, setForm] = useState({
    invoice_primary_color:  s.invoice_primary_color  || s.primary_color   || '#0B6E7F',
    invoice_accent_color:   s.invoice_accent_color   || s.secondary_color || '#FFC107',
    invoice_footer_text:    s.invoice_footer_text    || '',
    invoice_late_fee_text:  s.invoice_late_fee_text  || '',
    invoice_paid_stamp_text: s.invoice_paid_stamp_text || 'PAID',
    invoice_number_prefix:  s.invoice_number_prefix  || 'KW-',
    invoice_show_ofsted_block:   s.invoice_show_ofsted_block   !== false,
    invoice_show_company_details: s.invoice_show_company_details !== false,
    invoice_show_payment_details: s.invoice_show_payment_details !== false,
    invoice_ofsted_urn_override: s.invoice_ofsted_urn_override || '',
  });
  const [showPaid, setShowPaid] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);

  const refreshPreview = useCallback(async () => {
    setRefreshing(true);
    try {
      const blob = await api.previewInvoicePdf({
        settings_overrides: form,
        show_paid_stamp: showPaid,
      });
      const url = URL.createObjectURL(blob);
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (err) { toast.error(err?.message || 'Preview failed'); }
    finally { setRefreshing(false); }
  }, [form, showPaid]);

  // Debounced live preview when fields change.
  useEffect(() => {
    const t = setTimeout(refreshPreview, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.invoice_primary_color, form.invoice_accent_color,
    form.invoice_footer_text, form.invoice_late_fee_text,
    form.invoice_paid_stamp_text,
    form.invoice_show_ofsted_block, form.invoice_show_company_details,
    form.invoice_show_payment_details, form.invoice_ofsted_urn_override,
    showPaid,
  ]);

  // On unmount, revoke.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.updateMyOrganization({
        settings: { ...(organization?.settings || {}), ...form },
      });
      toast.success('Invoice branding saved');
      onSaved && onSaved(updated);
    } catch (err) { toast.error(err?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" data-testid="invoice-branding-studio" style={{ marginTop: '1rem' }}>
      <h3 style={{ margin: '0 0 0.15rem' }}>🎨 Invoice branding studio</h3>
      <p style={{ margin: '0 0 0.75rem', color: 'hsl(var(--muted-foreground))', fontSize: '0.82rem' }}>
        Customise the invoice PDF — colours, footer blocks (Ofsted, company, bank), paid stamp wording, number prefix. Preview updates live.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1rem' }}>
        {/* Controls */}
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div className="form-group"><label>Primary colour</label>
              <input type="color" value={form.invoice_primary_color} onChange={(e) => setForm({ ...form, invoice_primary_color: e.target.value })} data-testid="branding-primary-color" style={{ height: 36, width: '100%', padding: 0 }} />
            </div>
            <div className="form-group"><label>Accent colour</label>
              <input type="color" value={form.invoice_accent_color} onChange={(e) => setForm({ ...form, invoice_accent_color: e.target.value })} data-testid="branding-accent-color" style={{ height: 36, width: '100%', padding: 0 }} />
            </div>
          </div>
          <div className="form-group"><label>Number prefix</label>
            <input type="text" value={form.invoice_number_prefix} onChange={(e) => setForm({ ...form, invoice_number_prefix: e.target.value })} placeholder="KW-" data-testid="branding-prefix" />
          </div>
          <div className="form-group"><label>Paid stamp wording</label>
            <input type="text" value={form.invoice_paid_stamp_text} onChange={(e) => setForm({ ...form, invoice_paid_stamp_text: e.target.value })} placeholder="PAID" maxLength={20} data-testid="branding-paid-stamp" />
          </div>
          <div className="form-group"><label>Late-fee clause (small print)</label>
            <input type="text" value={form.invoice_late_fee_text} onChange={(e) => setForm({ ...form, invoice_late_fee_text: e.target.value })} placeholder="Late fees of £2.50 per late minute apply after 18:30" data-testid="branding-late-fee" />
          </div>
          <div className="form-group"><label>Footer text</label>
            <input type="text" value={form.invoice_footer_text} onChange={(e) => setForm({ ...form, invoice_footer_text: e.target.value })} placeholder="Registered Charity No. 12345 · VAT GB1234" data-testid="branding-footer" />
          </div>
          <div className="form-group" style={{ padding: '0.6rem 0.75rem', background: 'hsl(210 30% 97%)', border: '1px solid hsl(var(--border))', borderRadius: 8 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'hsl(var(--muted-foreground))', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
              Footer blocks
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.35rem' }}>
              <input type="checkbox" checked={form.invoice_show_ofsted_block} onChange={(e) => setForm({ ...form, invoice_show_ofsted_block: e.target.checked })} data-testid="branding-show-ofsted" />
              <span>Show Ofsted registration <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.78rem' }}>— e.g. OFSTED REGISTERED  ¦  URN EY26424</span></span>
            </label>
            {form.invoice_show_ofsted_block && (
              <div style={{ marginLeft: '1.6rem', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  value={form.invoice_ofsted_urn_override}
                  onChange={(e) => setForm({ ...form, invoice_ofsted_urn_override: e.target.value })}
                  placeholder={organization?.ofsted_urn ? `URN override (currently: ${organization.ofsted_urn})` : 'Ofsted URN (e.g. EY26424)'}
                  data-testid="branding-ofsted-urn"
                  style={{ fontSize: '0.82rem' }}
                />
                <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
                  Leave blank to use org record URN. This value only affects invoices.
                </div>
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.35rem' }}>
              <input type="checkbox" checked={form.invoice_show_company_details} onChange={(e) => setForm({ ...form, invoice_show_company_details: e.target.checked })} data-testid="branding-show-company" />
              <span>Show company details <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.78rem' }}>— Legal name, Co. Number, VAT</span></span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.invoice_show_payment_details} onChange={(e) => setForm({ ...form, invoice_show_payment_details: e.target.checked })} data-testid="branding-show-payment" />
              <span>Show payment details <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.78rem' }}>— Bank name, sort code, account, IBAN</span></span>
            </label>
            <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
              Sourced from Org Settings → Company details & Bank details. Update those to change what shows here.
            </div>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={showPaid} onChange={(e) => setShowPaid(e.target.checked)} data-testid="branding-preview-paid" />
              Preview PAID stamp
            </label>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-ghost" onClick={refreshPreview} disabled={refreshing} data-testid="branding-refresh" style={{ fontSize: '0.82rem' }}>🔄 Refresh preview</button>
            {previewUrl && (
              <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost" data-testid="branding-open-in-new-tab" style={{ fontSize: '0.82rem', textDecoration: 'none' }}>↗ Open in new tab</a>
            )}
            <button type="button" className="btn btn-primary" onClick={save} disabled={saving} data-testid="branding-save">{saving ? 'Saving…' : '💾 Save branding'}</button>
          </div>
        </div>

        {/* Preview */}
        <div style={{ border: '1px solid hsl(var(--border))', borderRadius: 8, overflow: 'hidden', background: '#f9fafb', minHeight: 480, position: 'relative' }} data-testid="branding-preview-frame">
          {previewUrl ? (
            <iframe title="Invoice preview" src={previewUrl} style={{ width: '100%', height: 620, border: 'none', background: 'white' }} />
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>Loading preview…</div>
          )}
          {refreshing && (
            <div style={{ position: 'absolute', top: 8, right: 8, padding: '0.2rem 0.5rem', background: 'hsl(var(--primary))', color: 'white', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700 }}>Rendering…</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RecurringSchedulesSection;
